import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Eye, RefreshCw } from "lucide-react";

export default function Dashboard() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get("/bookings/mine")
      .then(r => setBookings(r.data.bookings))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!ready || !user) return;
    refresh();
    const iv = setInterval(refresh, 20000);
    return () => clearInterval(iv);
  }, [ready, user, refresh]);

  if (ready && !user) return <Navigate to="/login" replace />;
  if (!ready || !user) return <div className="sv-dash"><div className="spinner" /></div>;

  const updateStatus = async (id, status) => {
    await api.patch(`/bookings/${id}/status`, { status });
    refresh();
    setSelected(null);
  };

  const summary = {
    new: bookings.filter(b => b.status === "new").length,
    confirmed: bookings.filter(b => b.status === "confirmed").length,
    completed: bookings.filter(b => b.status === "completed").length,
  };

  return (
    <div data-testid="dashboard-page">
      <Nav theme="light" />
      <div className="sv-dash">
        <div className="container-x">
          <span className="eyebrow eyebrow-navy">Your dashboard</span>
          <h1 data-testid="dashboard-greeting">Welcome, <span className="em-italic-navy">{user.name.replace("Dr. ", "Dr. ")}</span></h1>
          <p className="sv-dash-sub">Live view of sleep studies booked with you through Somveil.</p>

          <div className="dash-stat-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 32 }}>
            <div className="sv-score-card"><div className="n">{bookings.length}</div><div className="l">Total bookings</div></div>
            <div className="sv-score-card"><div className="n" style={{ color: "var(--teal)" }}>{summary.new}</div><div className="l">New · to confirm</div></div>
            <div className="sv-score-card"><div className="n" style={{ color: "#8F6A00" }}>{summary.confirmed}</div><div className="l">Confirmed</div></div>
            <div className="sv-score-card"><div className="n" style={{ color: "#0F6E56" }}>{summary.completed}</div><div className="l">Completed</div></div>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-60)" }}>
              Bookings · auto-refreshed every 20s
            </div>
            <button className="btn btn-ghost" style={{ padding: "8px 16px", fontSize: 13 }} onClick={refresh} data-testid="refresh-bookings-btn">
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          {loading && bookings.length === 0 && <div className="spinner" />}
          {!loading && bookings.length === 0 && (
            <div style={{ textAlign: "center", padding: 80, background: "white", borderRadius: "var(--radius-lg)", border: "1px solid var(--line)", color: "var(--ink-60)" }} data-testid="no-bookings">
              No sleep studies booked yet. You&apos;ll see them here in real-time as patients book.
            </div>
          )}

          <div data-testid="bookings-list">
            {bookings.map(b => (
              <div key={b.id} className="sv-booking-row" data-testid={`booking-row-${b.id}`}>
                <div>
                  <div className="k">Patient</div>
                  <div className="name">{b.patient_name}</div>
                  <div className="v" style={{ marginTop: 2 }}>{b.patient_city} · {b.patient_phone}</div>
                </div>
                <div>
                  <div className="k">Test · Date</div>
                  <div className="v">Home sleep study</div>
                  <div className="v" style={{ marginTop: 2 }}>{b.preferred_date}</div>
                </div>
                <div>
                  <div className="k">Quiz risk</div>
                  <div className="v">{b.quiz_result?.riskLabel || "Not taken"}</div>
                  <div className="v" style={{ marginTop: 2 }}>{b.quiz_result ? `${b.quiz_result.percentageScore}% score` : "—"}</div>
                </div>
                <div>
                  <div className="k">Status</div>
                  <span className={`sv-badge-pill ${b.status}`}>{b.status}</span>
                </div>
                <div>
                  <button className="btn btn-ghost" style={{ padding: "8px 14px", fontSize: 13, width: "100%" }} onClick={() => setSelected(b)} data-testid={`view-booking-${b.id}`}>
                    <Eye size={13} /> View
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selected && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(10,26,44,0.6)", backdropFilter: "blur(8px)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={() => setSelected(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "white", borderRadius: "var(--radius-lg)", padding: 40, maxWidth: 640, width: "100%", maxHeight: "90vh", overflowY: "auto" }}
            data-testid="booking-detail-modal"
          >
            <span className="eyebrow eyebrow-navy">Booking detail</span>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 28, margin: "10px 0", fontWeight: 400 }}>{selected.patient_name}</h2>
            <div className="form-2col-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 20 }}>
              <div className="sv-score-card"><div className="l">Email</div><div style={{ fontSize: 14, color: "var(--navy)", marginTop: 4 }}>{selected.patient_email}</div></div>
              <div className="sv-score-card"><div className="l">Phone</div><div style={{ fontSize: 14, color: "var(--navy)", marginTop: 4 }}>{selected.patient_phone}</div></div>
              <div className="sv-score-card"><div className="l">City</div><div style={{ fontSize: 14, color: "var(--navy)", marginTop: 4 }}>{selected.patient_city}</div></div>
              <div className="sv-score-card"><div className="l">Test · Price</div><div style={{ fontSize: 14, color: "var(--navy)", marginTop: 4 }}>Home sleep study · ₹{selected.test_price_inr?.toLocaleString()}</div></div>
              <div className="sv-score-card"><div className="l">Preferred date</div><div style={{ fontSize: 14, color: "var(--navy)", marginTop: 4 }}>{selected.preferred_date}</div></div>
              <div className="sv-score-card"><div className="l">Status</div><div style={{ marginTop: 4 }}><span className={`sv-badge-pill ${selected.status}`}>{selected.status}</span></div></div>
            </div>
            {selected.patient_address && <div style={{ marginBottom: 20 }}><div className="sv-label">Home address for technician visit</div><div style={{ padding: 14, background: "var(--cream)", borderRadius: 10, fontSize: 14, lineHeight: 1.5 }}>{selected.patient_address}</div></div>}
            {selected.notes && <div style={{ marginBottom: 20 }}><div className="sv-label">Patient notes</div><div style={{ padding: 14, background: "var(--cream)", borderRadius: 10, fontSize: 14 }}>{selected.notes}</div></div>}
            {selected.quiz_result && (
              <div>
                <div className="sv-label">Quiz risk assessment</div>
                <div style={{ padding: 16, background: "var(--cream)", borderRadius: 10, marginBottom: 20 }}>
                  <span className={`sv-risk-badge ${selected.quiz_result.riskTier}`} style={{ fontSize: 10, padding: "4px 12px", marginBottom: 8 }}>{selected.quiz_result.riskLabel}</span>
                  <div style={{ fontSize: 14, marginBottom: 12 }}>Score {selected.quiz_result.percentageScore}% · {selected.quiz_result.highFlagCount} high-priority · {selected.quiz_result.secondaryFlagCount} secondary flags</div>
                  <div style={{ fontSize: 13, color: "var(--ink-60)" }}>{selected.quiz_result.summary}</div>
                  {selected.quiz_result.flaggedSymptoms?.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      {selected.quiz_result.flaggedSymptoms.map((f, i) => (
                        <div key={f.id || f.text} className={`sv-flag-item ${f.type}`} style={{ marginBottom: 6 }}>
                          <span className="dot" /><span className="t" style={{ fontSize: 13 }}>{f.text}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {selected.status === "new" && <button className="btn btn-primary" onClick={() => updateStatus(selected.id, "confirmed")} data-testid="mark-confirmed">Mark confirmed</button>}
              {selected.status === "confirmed" && <button className="btn btn-primary" onClick={() => updateStatus(selected.id, "completed")} data-testid="mark-completed">Mark completed</button>}
              {selected.status !== "cancelled" && selected.status !== "completed" && <button className="btn btn-ghost" onClick={() => updateStatus(selected.id, "cancelled")} data-testid="mark-cancelled">Cancel</button>}
              <button className="btn btn-ghost" style={{ marginLeft: "auto" }} onClick={() => setSelected(null)} data-testid="close-modal">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
